// ─────────────────────────────────────────────────────────────────────────────
//  ДОГОВОР между човешкия одит изглед и API контракта (ЕДНО МЯСТО ЗА ПРОМЯНА).
//
//  Одитът пази СНИМКА на HTTP заявката/отговора (request_body / response_body),
//  а не директни DB колони. Затова човешкият слой е вързан за ИМЕНАТА НА ПОЛЕТАТА
//  в тези payload-и, за ПЪТИЩАТА на route-овете и за няколко МАГИЧЕСКИ СТОЙНОСТИ.
//
//  ⚠️  Ако в бекенда/фронтенда се преименува route, поле в заявката/отговора или
//      се смени сентинел (напр. billId за „нова сметка"), ПРОМЕНИ ГО ТУК.
//      Всичко по-долу се чете от нетипизиран JSON, затова TypeScript НЯМА да хване
//      разминаването — единственото защитно мрежо е този файл + graceful fallback.
//
//  Забележка: имената от менюто/плащанията (itemLangs, translations, name_bg…) НЕ
//  са тук, защото идват от типизирани API интерфейси (api/menu.ts, api/
//  paymentMethods.ts) — там TypeScript хваща преименуване по време на компилация.
// ─────────────────────────────────────────────────────────────────────────────

// ── Пътища / класификация ────────────────────────────────────────────────────

export const PATHS = {
  /** Колекция сметки. POST с billId === 0 → нова, иначе добавяне. */
  billsCollection: "/bills",
  cashIn: "/operations/cash-in",
  cashOut: "/operations/cash-out",
  /** PATCH — всички отворени маси на текущия потребител към друг сервитьор. */
  transferAllWaiter: "/bills/waiter/transfer-all",
  /** /bills/{id}/<action> — група 1 е действието. */
  billSubpath: /^\/bills\/\d+\/([a-z-]+)$/,
  /** /bills/{id}/<a>/<b> — двусегментните: close/retry, storno/retry, preliminary-bill/print. */
  billSubpath2: /^\/bills\/\d+\/([a-z-]+)\/([a-z-]+)$/,
} as const;

/** Подпът `/bills/{id}/<action>` → вътрешна категория (kind). */
export const BILL_ACTION_KIND: Record<string, string> = {
  "apply-discount": "applyDiscount",
  "remove-discount": "removeDiscount",
  "apply-item-discounts": "applyItemDiscounts",
  // старите единични endpoint-и — исторически записи в одита
  "apply-item-discount": "applyItemDiscount",
  "remove-item-discount": "removeItemDiscount",
  "set-item-prices": "setItemPrices",
  "cancel-item": "cancelItem",
  // групово сторно (0.0.274) — един запис за всички редове на действието
  "cancel-items": "cancelItems",
  "cancel-all-items": "cancelAll",
  close: "close",
  restore: "restore",
  split: "split",
  "split-and-pay": "splitAndPay",
  name: "rename",
  waiter: "changeWaiter",
};

/** Двусегментен подпът `/bills/{id}/<a>/<b>` → категория. Ключът е „a/b". */
export const BILL_ACTION_KIND_2: Record<string, string> = {
  "close/retry": "fiscalRetry",
  "storno/retry": "stornoRetry",
  "preliminary-bill/print": "preliminaryPrint",
};

// ── Пътища на модулите извън „Маркиране" ─────────────────────────────────────
//
// Класификацията е по (метод, път) — етикетът `operation` е локализиран низ,
// генериран от BE allowlist-а, и НИКОГА не се ползва за разпознаване.

export const MODULE_PATHS = {
  /** /inventory/<документ>[/{id}] — група 1 е видът, група 2 е ид-то при PUT/DELETE. */
  inventoryDoc:
    /^\/inventory\/(deliveries|writeoffs|stocktakes|transfers|report-postings|interchange-groups)(?:\/(\d+))?$/,
  inventoryRestore: /^\/inventory\/deleted\/(\d+)\/restore$/,
  /** POST — действията със стоковите разписки; група 1 е действието (BE `service_delivery_notes.py`). */
  inventoryDeliveryNotes:
    /^\/inventory\/deliveries\/(detach-from-invoice|close-without-invoice|reopen-for-invoice)$/,
  inventorySettings: "/inventory/settings",
  /** PUT/DELETE /inventory/product-warehouses/{productId} — „Склад по продукт". */
  inventoryProductWarehouse: /^\/inventory\/product-warehouses\/(\d+)$/,
  /** /configuration/<настройка> — група 1 е името на настройката. */
  configuration: /^\/configuration\/([a-z-]+)$/,
  invoicePrint: /^\/invoices\/(\d+)\/print$/,
  establishmentPrint: /^\/reports\/establishment\/(\d+)\/print$/,
  voiceParseOrder: "/voice/parse-order",
  deviceFiscalUpdate: /^\/devices\/(\d+)\/fiscalapi\/update$/,
  /** POST — нова резервация. */
  reservationsCollection: "/reservations",
  /** PATCH /reservations/{id} — редакция. */
  reservationItem: /^\/reservations\/(\d+)$/,
  /** POST /reservations/{id}/<действие> — група 2 е действието. */
  reservationAction: /^\/reservations\/(\d+)\/([a-z-]+)$/,
} as const;

/** Действие по резервация от пътя → вътрешна категория (kind). */
export const RESERVATION_ACTION_KIND: Record<string, string> = {
  seat: "resSeat",
  // „Настани" от Маркиране — същият отговор (`billId`, `billCreated`).
  "seat-from-ordering": "resSeat",
  release: "resRelease",
  unseat: "resUnseat",
  cancel: "resCancel",
  restore: "resRestore",
};

/** Вид складов документ от пътя → вътрешна категория (kind). */
export const INVENTORY_DOC_KIND: Record<string, string> = {
  deliveries: "invDelivery",
  writeoffs: "invWriteoff",
  stocktakes: "invStocktake",
  transfers: "invTransfer",
  "report-postings": "invReportPosting",
  "interchange-groups": "invInterchange",
};

// ── Полета в request_body ────────────────────────────────────────────────────

export const REQ = {
  billId: "billId",
  elements: "elements",
  comment: "comment",
  // продуктов елемент
  productId: "productId",
  stationId: "stationId",
  quantity: "quantity",
  price: "price",
  percent: "percent",
  lineTotal: "lineTotal",
  modifiers: "modifiers",
  modifierId: "modifier_id",
  addons: "addons",
  addonItemId: "addition_item_id",
  addonTotal: "total_sum",
  // отстъпка
  clientName: "clientName",
  discountType: "discountType",
  billElementItemId: "billElementItemId",
  // промяна на цени / отстъпки по продукти — `lines` е ключът и в
  // set-item-prices, и в apply-item-discounts
  priceLines: "lines",
  additionId: "additionId",
  // сторно
  cancelLines: "items",
  reason: "reason",
  isDefective: "is_defective",
  isDefectiveAlt: "isDefective",
  // затваряне / плащане
  paymentMethodId: "paymentMethodId",
  mixedCash: "mixedCashAmount",
  mixedCard: "mixedCardAmount",
  total: "total",
  // разделяне / преименуване / сервитьор
  splitTableName: "splitTableName",
  // id на bill-element-item в split заявката (въпреки името "billElementId")
  splitBillElementId: "billElementId",
  name: "name",
  waiterId: "waiterId",
  // каса — взимаме първото налично от тези полета
  cashAmountFields: ["amount", "sum", "value", "total"],
} as const;

// ── Полета в response_body ───────────────────────────────────────────────────

export const RES = {
  id: "id",
  elements: "elements",
  items: "items",
  itemId: "item_id",
  itemNames: "item_names",
  price: "price",
  quantity: "quantity",
  /** Салдо на сметката след операцията — основа за timeline баланса/делтите. */
  totalAmount: "total_amount",
  fiscalResult: "fiscal_result",
  detail: "detail",
  // split връща { oldBill, newBill } — двете сметки след разделянето.
  splitOldBill: "oldBill",
  splitNewBill: "newBill",
} as const;

export const FISCAL = {
  statusField: "status",
  errorField: "error",
  /** Стойност на fiscal_result.status, която значи провал (дори при HTTP 200). */
  failedValue: "failed",
} as const;

// Маркери, които middleware-ът слага при отрязване на твърде голямо тяло.
export const TRUNCATED = {
  flag: "__truncated__",
  size: "__original_size__",
} as const;

// ── Полета в складовите payload-и ────────────────────────────────────────────
//
// Складовите заявки са camelCase (`warehouseId`), защото минават през същите
// pydantic alias-и като останалия REST контракт. Редовете вътре в `lines` също.

export const INV = {
  warehouseId: "warehouseId",
  fromWarehouseId: "fromWarehouseId",
  toWarehouseId: "toWarehouseId",
  supplierId: "supplierId",
  invoiceNumber: "invoiceNumber",
  reportId: "reportId",
  defaultWarehouseId: "defaultWarehouseId",
  vatRegistered: "vatRegistered",
  note: "note",
  comment: "comment",
  reason: "reason",
  hint: "hint",
  kind: "kind",
  lines: "lines",
  members: "members",
  substitutions: "substitutions",
  productId: "productId",
  recipeId: "recipeId",
  quantity: "quantity",
  counted: "counted",
  unitPrice: "unitPrice",
  packagingsCount: "packagingsCount",
  coefficient: "coefficient",
  /** Действията със стоковите: `ids`, `reason`, `payment: {paidAt, method}`. */
  ids: "ids",
  payment: "payment",
  paidAt: "paidAt",
  paymentMethod: "method",
  /** `kind` на целия документ: "writeoff" | "scrap" (бракуване). */
  scrapKind: "scrap",
  /** `kind` на ред в изписването: "product" | "recipe". */
  recipeLineKind: "recipe",
} as const;

/** Полета в телата на `PUT /configuration/*`. */
export const CFG = {
  view: "view",
  enabled: "enabled",
} as const;

/** Полета в заявката/отговора на `POST /voice/parse-order`. */
export const VOICE = {
  transcript: "transcript",
  tableName: "tableName",
  items: "items",
  spokenName: "spokenName",
  quantity: "quantity",
  productIds: "productIds",
  comment: "comment",
} as const;

/**
 * Полета на резервацията — `response_body` на всяка мутация под
 * `/reservations` е самата резервация (camelCase), а `request_body` на
 * създаване/редакция — формата. „Настани" добавя `billCreated` (нова празна
 * сметка vs закачена към отворена); `billId` сочи сметката и в двата случая
 * (по него редът получава и `bill_id`/`table_name`), а `billIds` — всички
 * сметки при „Отделна сметка за всяка маса". „Отмени настаняването"
 * добавя `billClosed` (празната сметка от „Настани" е премахната).
 * BE: reservations/schemas.py.
 */
export const RSV = {
  guestName: "guestName",
  guests: "guests",
  startAt: "startAt",
  durationMinutes: "durationMinutes",
  tableNames: "tableNames",
  tableIds: "tableIds",
  note: "note",
  billId: "billId",
  billIds: "billIds",
  billCreated: "billCreated",
  billClosed: "billClosed",
} as const;

/** Envelope-ът {request_id, status, error} на MQTT командите. */
export const ENVELOPE = {
  requestId: "request_id",
  status: "status",
  error: "error",
} as const;

// ── Магически стойности ──────────────────────────────────────────────────────

/** billId === NEW_BILL_ID в POST /bills → отваря НОВА сметка (vs. добавяне). */
export const NEW_BILL_ID = 0;
